/**
 * Investment Importer Component
 *
 * Modal for importing investments from an Excel file.
 * Parses Excel data, validates, and creates investment records.
 */

import { usePortcos, useSegments, useCreateInvestment } from '@medsuite/api-client';
import { Button, Alert } from '@medsuite/ui-components';
import { useState, useRef } from 'react';
import * as XLSX from 'xlsx';

interface ImportedRow {
  rowNumber: number;
  title: string;
  portcoName: string;
  segmentName: string;
  regulatoryRequirement: string;
  primeObjective?: string;
  problemContext?: string;
  improvementIdeas?: string;
  assumptions?: string;
  // Resolved IDs
  portcoId?: number;
  segmentId?: number;
  // Validation
  errors: string[];
  isValid: boolean;
}

interface ImportResult {
  success: number;
  failed: number;
  errors: string[];
}

// Expected Excel column headers (case-insensitive matching)
const COLUMN_MAPPINGS: Record<
  string,
  keyof Omit<ImportedRow, 'rowNumber' | 'portcoId' | 'segmentId' | 'errors' | 'isValid'>
> = {
  'investment title': 'title',
  title: 'title',
  'portfolio company': 'portcoName',
  portco: 'portcoName',
  segment: 'segmentName',
  'regulatory requirement': 'regulatoryRequirement',
  regulatory: 'regulatoryRequirement',
  'prime objective': 'primeObjective',
  objective: 'primeObjective',
  'problem & context': 'problemContext',
  'problem context': 'problemContext',
  'problem and context': 'problemContext',
  'improvement ideas': 'improvementIdeas',
  improvements: 'improvementIdeas',
  'assumptions & constraints': 'assumptions',
  'assumptions and constraints': 'assumptions',
  assumptions: 'assumptions',
  constraints: 'assumptions',
};

interface InvestmentImporterProps {
  isOpen: boolean;
  onClose: () => void;
  onImportComplete: (result: ImportResult) => void;
}

export function InvestmentImporter({ isOpen, onClose, onImportComplete }: InvestmentImporterProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { data: portcos = [] } = usePortcos();
  const { data: segments = [] } = useSegments();
  const createInvestment = useCreateInvestment();

  const [parsedRows, setParsedRows] = useState<ImportedRow[]>([]);
  const [parseError, setParseError] = useState<string | null>(null);
  const [isImporting, setIsImporting] = useState(false);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [step, setStep] = useState<'upload' | 'preview' | 'complete'>('upload');

  const resetState = () => {
    setParsedRows([]);
    setParseError(null);
    setIsImporting(false);
    setImportResult(null);
    setStep('upload');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  const resolvePortcoId = (name: string): number | undefined => {
    const normalizedName = name.toLowerCase().trim();
    const match = portcos.find((p) => p.portco_name.toLowerCase().trim() === normalizedName);
    return match?.portco_id;
  };

  const resolveSegmentId = (name: string): number | undefined => {
    const normalizedName = name.toLowerCase().trim();
    const match = segments.find((s) => s.segment_name.toLowerCase().trim() === normalizedName);
    return match?.segment_id;
  };

  const validateRow = (row: ImportedRow): ImportedRow => {
    const errors: string[] = [];

    // Required field validation
    if (!row.title?.trim()) {
      errors.push('Investment Title is required');
    }
    if (!row.portcoName?.trim()) {
      errors.push('Portfolio Company is required');
    }
    if (!row.segmentName?.trim()) {
      errors.push('Segment is required');
    }
    if (!row.regulatoryRequirement?.trim()) {
      errors.push('Regulatory Requirement is required');
    } else {
      const reg = row.regulatoryRequirement.toUpperCase().trim();
      if (reg !== 'Y' && reg !== 'N' && reg !== 'YES' && reg !== 'NO') {
        errors.push('Regulatory Requirement must be Y or N');
      }
    }

    // Resolve IDs
    let portcoId: number | undefined;
    let segmentId: number | undefined;

    if (row.portcoName?.trim()) {
      portcoId = resolvePortcoId(row.portcoName);
      if (!portcoId) {
        errors.push(`Portfolio Company "${row.portcoName}" not found`);
      }
    }

    if (row.segmentName?.trim()) {
      segmentId = resolveSegmentId(row.segmentName);
      if (!segmentId) {
        errors.push(`Segment "${row.segmentName}" not found`);
      }
    }

    return {
      ...row,
      portcoId,
      segmentId,
      errors,
      isValid: errors.length === 0,
    };
  };

  const parseExcelFile = (file: File) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const data = e.target?.result;
        const workbook = XLSX.read(data, { type: 'array' });

        // Get the first sheet
        const sheetName = workbook.SheetNames[0];
        const sheet = workbook.Sheets[sheetName];

        // Convert to JSON with header row
        const jsonData = XLSX.utils.sheet_to_json<Record<string, unknown>>(sheet, {
          defval: '',
        });

        if (jsonData.length === 0) {
          setParseError('No data found in the Excel file');
          return;
        }

        // Map columns to expected fields
        const rows: ImportedRow[] = jsonData.map((row, index) => {
          const mappedRow: Partial<ImportedRow> = {
            rowNumber: index + 2, // +2 because row 1 is header, and we're 0-indexed
            errors: [],
            isValid: false,
          };

          // Map each column header to our expected field
          Object.keys(row).forEach((header) => {
            const normalizedHeader = header.toLowerCase().trim();
            const fieldName = COLUMN_MAPPINGS[normalizedHeader];
            if (fieldName) {
              (mappedRow as Record<string, unknown>)[fieldName] = String(row[header] || '').trim();
            }
          });

          return mappedRow as ImportedRow;
        });

        // Validate all rows
        const validatedRows = rows.map(validateRow);
        setParsedRows(validatedRows);
        setParseError(null);
        setStep('preview');
      } catch (err) {
        console.error('Failed to parse Excel file:', err);
        setParseError(
          'Failed to parse Excel file. Please ensure it is a valid .xlsx or .xls file.'
        );
      }
    };

    reader.onerror = () => {
      setParseError('Failed to read the file');
    };

    reader.readAsArrayBuffer(file);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      parseExcelFile(file);
    }
  };

  const handleImport = async () => {
    const validRows = parsedRows.filter((row) => row.isValid);
    if (validRows.length === 0) {
      return;
    }

    setIsImporting(true);
    const result: ImportResult = {
      success: 0,
      failed: 0,
      errors: [],
    };

    for (const row of validRows) {
      try {
        const regValue = row.regulatoryRequirement.toUpperCase().trim();
        const isRegulatory = regValue === 'Y' || regValue === 'YES';

        await createInvestment.mutateAsync({
          title: row.title,
          portco_id: row.portcoId!,
          segment_id: row.segmentId!,
          is_regulatory_requirement: isRegulatory,
          prime_objective: row.primeObjective || undefined,
          problem_context: row.problemContext || undefined,
          improvement_ideas: row.improvementIdeas || undefined,
          assumptions: row.assumptions || undefined,
        });
        result.success++;
      } catch (err) {
        result.failed++;
        result.errors.push(`Row ${row.rowNumber} (${row.title}): ${(err as Error).message}`);
      }
    }

    setImportResult(result);
    setIsImporting(false);
    setStep('complete');
    onImportComplete(result);
  };

  const validCount = parsedRows.filter((r) => r.isValid).length;
  const invalidCount = parsedRows.filter((r) => !r.isValid).length;

  // Generate and download Excel template
  const handleDownloadTemplate = () => {
    // Define the column headers for the template
    const headers = [
      'Investment Title',
      'Portfolio Company',
      'Segment',
      'Regulatory Requirement',
      'Prime Objective',
      'Problem & Context',
      'Improvement Ideas',
      'Assumptions & Constraints',
    ];

    // Create worksheet with headers only
    const ws = XLSX.utils.aoa_to_sheet([headers]);

    // Set column widths for better readability
    ws['!cols'] = [
      { wch: 30 }, // Investment Title
      { wch: 20 }, // Portfolio Company
      { wch: 15 }, // Segment
      { wch: 22 }, // Regulatory Requirement
      { wch: 40 }, // Prime Objective
      { wch: 40 }, // Problem & Context
      { wch: 40 }, // Improvement Ideas
      { wch: 40 }, // Assumptions & Constraints
    ];

    // Create workbook and add the worksheet
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, ws, 'Investments');

    // Generate and download the file
    XLSX.writeFile(workbook, 'investment_import_template.xlsx');
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/50 transition-opacity" onClick={handleClose} />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative w-full max-w-4xl transform rounded-lg bg-white dark:bg-gray-800 shadow-xl transition-all">
          {/* Header */}
          <div className="flex items-center justify-between border-b border-gray-200 dark:border-gray-700 px-6 py-4">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Import Investments from Excel
            </h2>
            <button
              onClick={handleClose}
              className="rounded p-1 text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-600 dark:hover:text-gray-200"
            >
              <svg
                className="h-5 w-5"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth="2"
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Content */}
          <div className="px-6 py-4">
            {step === 'upload' && (
              <div className="space-y-4">
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                  <h3 className="text-sm font-semibold text-blue-800 dark:text-blue-200 mb-2">
                    Excel File Format
                  </h3>
                  <p className="text-sm text-blue-700 dark:text-blue-300 mb-2">
                    Your Excel file should have a header row with the following columns:
                  </p>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div>
                      <span className="font-medium text-blue-800 dark:text-blue-200">
                        Required:
                      </span>
                      <ul className="list-disc list-inside text-blue-700 dark:text-blue-300 ml-2">
                        <li>Investment Title</li>
                        <li>Portfolio Company</li>
                        <li>Segment</li>
                        <li>Regulatory Requirement</li>
                      </ul>
                    </div>
                    <div>
                      <span className="font-medium text-blue-800 dark:text-blue-200">
                        Optional:
                      </span>
                      <ul className="list-disc list-inside text-blue-700 dark:text-blue-300 ml-2">
                        <li>Prime Objective</li>
                        <li>Problem & Context</li>
                        <li>Improvement Ideas</li>
                        <li>Assumptions & Constraints</li>
                      </ul>
                    </div>
                  </div>
                </div>

                {parseError && <Alert variant="error">{parseError}</Alert>}

                <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-8 text-center">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={handleFileChange}
                    className="hidden"
                    id="excel-file-input"
                  />
                  <label htmlFor="excel-file-input" className="cursor-pointer">
                    <svg
                      className="mx-auto h-12 w-12 text-gray-400"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1.5}
                        d="M9 13h6m-3-3v6m5 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                      />
                    </svg>
                    <p className="mt-2 text-sm font-medium text-gray-900 dark:text-gray-100">
                      Click to select an Excel file
                    </p>
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      .xlsx or .xls files supported
                    </p>
                  </label>
                </div>
              </div>
            )}

            {step === 'preview' && (
              <div className="space-y-4">
                {/* Summary */}
                <div className="flex gap-4">
                  <div className="flex-1 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-3">
                    <div className="text-2xl font-bold text-green-700 dark:text-green-300">
                      {validCount}
                    </div>
                    <div className="text-sm text-green-600 dark:text-green-400">
                      Valid rows ready to import
                    </div>
                  </div>
                  {invalidCount > 0 && (
                    <div className="flex-1 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
                      <div className="text-2xl font-bold text-red-700 dark:text-red-300">
                        {invalidCount}
                      </div>
                      <div className="text-sm text-red-600 dark:text-red-400">
                        Rows with errors (will be skipped)
                      </div>
                    </div>
                  )}
                </div>

                {/* Preview Table */}
                <div className="max-h-96 overflow-auto border border-gray-200 dark:border-gray-700 rounded-lg">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-900 sticky top-0">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 dark:text-gray-300">
                          Row
                        </th>
                        <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 dark:text-gray-300">
                          Status
                        </th>
                        <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 dark:text-gray-300">
                          Investment Title
                        </th>
                        <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 dark:text-gray-300">
                          Portfolio Company
                        </th>
                        <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 dark:text-gray-300">
                          Segment
                        </th>
                        <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 dark:text-gray-300">
                          Regulatory
                        </th>
                        <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 dark:text-gray-300">
                          Errors
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700 bg-white dark:bg-gray-800">
                      {parsedRows.map((row) => (
                        <tr
                          key={row.rowNumber}
                          className={row.isValid ? '' : 'bg-red-50 dark:bg-red-900/10'}
                        >
                          <td className="px-3 py-2 text-sm text-gray-900 dark:text-gray-100">
                            {row.rowNumber}
                          </td>
                          <td className="px-3 py-2">
                            {row.isValid ? (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-200">
                                Valid
                              </span>
                            ) : (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 dark:bg-red-900/50 text-red-800 dark:text-red-200">
                                Error
                              </span>
                            )}
                          </td>
                          <td
                            className="px-3 py-2 text-sm text-gray-900 dark:text-gray-100 max-w-xs truncate"
                            title={row.title}
                          >
                            {row.title || <span className="text-gray-400">-</span>}
                          </td>
                          <td className="px-3 py-2 text-sm text-gray-900 dark:text-gray-100">
                            {row.portcoName || <span className="text-gray-400">-</span>}
                            {row.portcoId && (
                              <span className="ml-1 text-green-600 dark:text-green-400 text-xs">
                                (ID: {row.portcoId})
                              </span>
                            )}
                          </td>
                          <td className="px-3 py-2 text-sm text-gray-900 dark:text-gray-100">
                            {row.segmentName || <span className="text-gray-400">-</span>}
                            {row.segmentId && (
                              <span className="ml-1 text-green-600 dark:text-green-400 text-xs">
                                (ID: {row.segmentId})
                              </span>
                            )}
                          </td>
                          <td className="px-3 py-2 text-sm text-gray-900 dark:text-gray-100">
                            {row.regulatoryRequirement || <span className="text-gray-400">-</span>}
                          </td>
                          <td className="px-3 py-2 text-xs text-red-600 dark:text-red-400 max-w-xs">
                            {row.errors.length > 0 ? (
                              <ul className="list-disc list-inside">
                                {row.errors.map((err, idx) => (
                                  <li key={idx}>{err}</li>
                                ))}
                              </ul>
                            ) : (
                              <span className="text-gray-400">-</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {step === 'complete' && importResult && (
              <div className="space-y-4">
                <div className="text-center py-8">
                  {importResult.success > 0 ? (
                    <svg
                      className="mx-auto h-16 w-16 text-green-500"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  ) : (
                    <svg
                      className="mx-auto h-16 w-16 text-red-500"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  )}
                  <h3 className="mt-4 text-lg font-semibold text-gray-900 dark:text-gray-100">
                    Import Complete
                  </h3>
                  <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                    Successfully imported{' '}
                    <span className="font-bold text-green-600 dark:text-green-400">
                      {importResult.success}
                    </span>{' '}
                    investment{importResult.success !== 1 ? 's' : ''}.
                    {importResult.failed > 0 && (
                      <span className="text-red-600 dark:text-red-400">
                        {' '}
                        {importResult.failed} failed.
                      </span>
                    )}
                  </p>
                </div>

                {importResult.errors.length > 0 && (
                  <Alert variant="error">
                    <p className="font-semibold mb-2">Some imports failed:</p>
                    <ul className="list-disc list-inside text-sm">
                      {importResult.errors.map((err, idx) => (
                        <li key={idx}>{err}</li>
                      ))}
                    </ul>
                  </Alert>
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between border-t border-gray-200 dark:border-gray-700 px-6 py-4">
            {step === 'upload' && (
              <>
                <button
                  type="button"
                  onClick={handleDownloadTemplate}
                  className="inline-flex items-center text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 hover:underline"
                >
                  <svg
                    className="h-4 w-4 mr-1.5"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth="2"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                    />
                  </svg>
                  Download import template
                </button>
                <Button variant="outline" onClick={handleClose}>
                  Cancel
                </Button>
              </>
            )}

            {step === 'preview' && (
              <>
                <Button variant="outline" onClick={resetState}>
                  Back
                </Button>
                <div className="flex gap-3">
                  <Button variant="outline" onClick={handleClose}>
                    Cancel
                  </Button>
                  <Button onClick={handleImport} disabled={validCount === 0 || isImporting}>
                    {isImporting
                      ? 'Importing...'
                      : `Import ${validCount} Investment${validCount !== 1 ? 's' : ''}`}
                  </Button>
                </div>
              </>
            )}

            {step === 'complete' && (
              <>
                <div />
                <Button onClick={handleClose}>Close</Button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default InvestmentImporter;
